import React from "react";
import { Link, useParams } from "react-router-dom";
import PropTypes from "prop-types";

import { makeStyles } from "tss-react/mui";

import Grid from "@mui/material/Grid";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Tooltip from "@mui/material/Tooltip";
import Skeleton from "@mui/material/Skeleton";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import RefreshIcon from "@mui/icons-material/Refresh";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";

import Warning from "./Warning";
import KMKNavigateButton from "../../../Common/KMKNavigateButton";

import { navDataExplorer } from "../../../kmk/AppData";
import { OBJECT_TYPE_ROUTES } from "./JobData";
import { DATA_SOURCE } from "../../../../util/Data/DataModeling";

const useStyles = makeStyles()((theme) => ({
  Skeleton: {
    display: "inline-block",
    marginRight: "10px",
  },
  Alert: {
    marginTop: 12,
    paddingTop: 0,
    paddingBottom: 0,
    borderRadius: 20,
  },
}));

const ObjectSelection = ({
  selectAll,
  intermediate,
  objectList,
  objectType,
  loading,
  handleSelectAllChange,
  handleCheckboxChange,
  handleRefresh,
  exceptReadOnly,
}) => {
  const { classes } = useStyles();

  const { Workspace_id } = useParams();

  return (
    <>
      <Grid size={12}>
        <FormControlLabel
          sx={{ userSelect: "none", paddingBlock: "8px" }}
          control={<Checkbox checked={selectAll} indeterminate={intermediate} />}
          onChange={handleSelectAllChange}
          disabled={loading}
          label="Select All"
        />
        <IconButton onClick={handleRefresh} disabled={loading}>
          <RefreshIcon />
        </IconButton>
      </Grid>
      {loading ? (
        [1, 2, 3].map((v) => {
          return (
            <Grid
              key={v}
              size={{
                xs: 6,
                sm: 6,
                md: 4,
              }}
            >
              <Skeleton variant="rectangular" width={"40px"} height={"24px"} className={classes.Skeleton} />
              <Skeleton
                variant="rectangular"
                width={"calc(100% - 100px)"}
                height={"24px"}
                className={classes.Skeleton}
              />
            </Grid>
          );
        })
      ) : (
        <>
          {objectList.length !== 0 ? (
            objectList.map((source) => (
              <Grid
                key={source.id}
                style={{ paddingTop: 0, display: "flex", alignItems: "center" }}
                size={{
                  xs: 6,
                  sm: 4,
                  md: 3,
                }}
              >
                <FormControlLabel
                  sx={{ userSelect: "none", margin: "none", wordBreak: "break-all", marginRight: "10px" }}
                  control={<Checkbox checked={source.checked} />}
                  onChange={(event) => handleCheckboxChange(event, source.name)}
                  label={
                    <Box sx={{ display: "flex", flexWrap: "nowrap", alignItems: "center" }}>
                      <Tooltip
                        placement="top"
                        title={`Edit ${objectType === 1 ? DATA_SOURCE.singular : "Entity"}`}
                        arrow
                      >
                        <Link
                          to={`/my-workspaces/${Workspace_id}/${OBJECT_TYPE_ROUTES[objectType]}/${source.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {source.displayName}
                        </Link>
                      </Tooltip>
                      <Box sx={{ display: "flex", flexWrap: "nowrap" }}>
                        {source.jobNames.length !== 0 && (
                          <Tooltip
                            placement="top"
                            title={
                              <Warning
                                placeHolder={objectType === 1 ? DATA_SOURCE.singular.toLowerCase() : "entity"}
                                jobNames={source.jobNames || []}
                              />
                            }
                            arrow
                          >
                            <IconButton onClick={(e) => e.preventDefault()}>
                              <WarningAmberRoundedIcon color="warning" style={{ fontSize: "1.2rem" }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {source.isUnapplied === 0 && (
                          <KMKNavigateButton
                            iconButton
                            tooltipProps={{ title: "View in SQL Explorer", arrow: true }}
                            arrow
                            navigationProps={{
                              to: {
                                pathname: `/my-workspaces/${Workspace_id}/${navDataExplorer.link}/`,
                                search: `sidebarView=${encodeURIComponent("DataSet")}&objectName=${encodeURIComponent(
                                  source.name
                                )}&schema=${encodeURIComponent("MODEL")}&expandWS=true`,
                              },
                              target: "_blank",
                              rel: "noopener noreferrer",
                            }}
                          >
                            <TableChartOutlinedIcon color="secondary" style={{ fontSize: "1.2rem" }} />
                          </KMKNavigateButton>
                        )}
                      </Box>
                    </Box>
                  }
                />
              </Grid>
            ))
          ) : (
            <Grid size={12}>{`No ${objectType === 1 ? DATA_SOURCE.plural.toLowerCase() : "entities"} available`}</Grid>
          )}

          {exceptReadOnly && (
            <Box sx={{ width: "100%" }}>
              <Alert
                variant="outlined"
                icon={<InfoOutlinedIcon fontSize="inherit" />}
                className={classes.Alert}
                severity="info"
                sx={{ width: "fit-content" }}
              >
                Datasets that are set to 'Read-Only Data View' are not shown in this list.
              </Alert>
            </Box>
          )}
        </>
      )}
    </>
  );
};

ObjectSelection.propTypes = {
  selectAll: PropTypes.bool.isRequired,
  intermediate: PropTypes.bool.isRequired,
  objectList: PropTypes.array.isRequired,
  objectType: PropTypes.number.isRequired,
  loading: PropTypes.bool.isRequired,
  handleSelectAllChange: PropTypes.func.isRequired,
  handleCheckboxChange: PropTypes.func.isRequired,
  handleRefresh: PropTypes.func.isRequired,
};

export default React.memo(ObjectSelection);
